import { z } from 'zod'

/**
 * Frontend environment validation (Build Standard v4.1 Section 23).
 *
 * These are PUBLIC values (Vite ships VITE_* to the browser). The point of
 * validating them is fail-fast: a malformed/missing config surfaces at boot
 * with a clear message instead of a silent broken call deep in the UI.
 *
 * No secrets here. Secrets live only in the Worker (see workers/env.ts).
 * The Clerk PUBLISHABLE key is public by design (pk_...) and is meant to ship
 * to the browser. The Clerk SECRET key is a Worker secret and must NEVER be
 * VITE_-prefixed or appear in this file.
 */
const frontendEnvSchema = z.object({
  VITE_ACTIVE_POD: z.string().min(1, 'VITE_ACTIVE_POD must be set (e.g. "oxydise")'),
  VITE_API_URL: z.string().url('VITE_API_URL must be a valid URL'),
  VITE_CLERK_PUBLISHABLE_KEY: z
    .string()
    .min(1, 'VITE_CLERK_PUBLISHABLE_KEY must be set')
    .startsWith('pk_', 'VITE_CLERK_PUBLISHABLE_KEY must start with "pk_"'),
  VITE_SENTRY_DSN: z.string().optional().default(''),
})

const parsed = frontendEnvSchema.safeParse(import.meta.env)
if (!parsed.success) {
  // Surface a readable error at boot rather than failing silently later.
  const issues = parsed.error.issues
    .map((i) => `  - ${i.path.join('.')}: ${i.message}`)
    .join('\n')
  throw new Error(`Invalid frontend environment configuration:\n${issues}`)
}

export const env = parsed.data
